/**
 * POI Data Integration for Neighbourhood Insights
 *
 * Reuses Travel Time POI data for neighbourhood insights to avoid additional API calls.
 */

import { logError } from '@/helpers/LogHelper';
import { TravelTimeData } from '@/store/types/travel-time-state';

import { ComposerTranslation } from 'vue-i18n';
import { DEFAULT_PERSONA } from './neighbourhoodConstants';
import { POIContext } from './neighbourhoodDescriptionTypes';
import { formatPOIDataForAI } from './poiDataFormatter';

/**
 * Minimal interface for POI cache operations.
 * Accepts the store instance to avoid calling useNeighbourhoodStore()
 * outside of Vue component setup context and to prevent circular imports.
 */
export interface POICacheStore {
  getPOIData(listingId: string, persona: string): TravelTimeData | null;
  setPOICache(listingId: string, persona: string, poiData: TravelTimeData): void;
}

/**
 * Get POI data for neighbourhood insights using provided store instance
 */
export async function getPOIDataFromStore(params: {
  listingId: string;
  latitude: number;
  longitude: number;
  persona?: string;
  poiCacheStore?: POICacheStore;
  t: ComposerTranslation;
}): Promise<POIContext> {
  const { listingId, latitude, longitude, persona, poiCacheStore, t } = params;
  try {
    let poiData: TravelTimeData | null = null;
    const normalizedPersona = persona || DEFAULT_PERSONA;

    if (poiCacheStore) {
      poiData = poiCacheStore.getPOIData(listingId, normalizedPersona);
    }

    if (!poiData?.length) {
      poiData = await fetchPOIDataOnDemand(
        listingId,
        latitude,
        longitude,
        normalizedPersona,
        poiCacheStore,
      );
    }

    if (!poiData?.length) {
      return {};
    }

    return formatPOIDataForAI(poiData, t);
  } catch (error) {
    logError(
      'Failed to get POI data for neighbourhood insights',
      { listingId },
      error instanceof Error ? error : undefined,
    );
    return {};
  }
}

async function fetchPOIDataOnDemand(
  listingId: string,
  latitude: number,
  longitude: number,
  persona: string,
  poiCacheStore?: POICacheStore,
): Promise<TravelTimeData | null> {
  const { fetchSurroundingInfo, TravelTimeEntryPoint } =
    await import('@/helpers/travel-time-helpers');
  const { processPersonaSurroundingInfoResponse } = await import('@/store/modules/travel-time');

  const result = await fetchSurroundingInfo(listingId, latitude, longitude, {
    entryPoint: TravelTimeEntryPoint.NEIGHBOURHOOD,
    persona,
  });

  if (!result.data?.data) {
    return null;
  }

  const poiData = processPersonaSurroundingInfoResponse(result.data.data);

  if (!poiData?.length) {
    return null;
  }

  if (poiCacheStore) {
    poiCacheStore.setPOICache(listingId, persona, poiData);
  }

  return poiData;
}

const DEFAULT_DATA_SOURCE = 'google-places';
const POI_DATA_SOURCE = 'poi';

export function shouldUsePOIDataSource(): boolean {
  const dataSource = import.meta.env.VUE_NEIGHBOURHOOD_DATA_SOURCE || DEFAULT_DATA_SOURCE;
  return dataSource === POI_DATA_SOURCE;
}

export function getNeighbourhoodDataSource(): 'poi' | 'google-places' {
  const dataSource = import.meta.env.VUE_NEIGHBOURHOOD_DATA_SOURCE || DEFAULT_DATA_SOURCE;
  return dataSource === POI_DATA_SOURCE ? POI_DATA_SOURCE : DEFAULT_DATA_SOURCE;
}
